import { useCallback, useEffect, useRef, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useFragment, useMutation } from "react-relay";
import { graphql } from "relay-runtime";
import { logger } from "@/common/logger";
import EditMarkdownSection from "@/components/EditMarkdownSection";
import ForumTopics from "@/components/ForumTopics";
import SubjectSubscriptionButton from "@/components/SubjectSubscriptionButton";
import VotableOrderSelect from "@/components/VotableOrderSelect";
import { useAuth, useLoginRedirect } from "@/utils/auth";
import { getForumPath, PathSegment } from "@/utils/routing";
import { TopicVotableOrderContext, VotableOrder } from "@/utils/votableOrder";

import { PlusIcon } from "@/icons";
import { Link } from "@/kit/ui/link";
import { DESKTOP_MEDIA_QUERY } from "@/common/constants";
import { supportsPassiveEvents } from "@/utils/helpers";
import { useMediaQuery } from "@/utils/hooks";
import { cn } from "@/utils/tailwind";

import { TopicsPageFragment$key } from "./__generated__/TopicsPageFragment.graphql";
import { TopicsPageUpdateForumDescriptionMutation } from "./__generated__/TopicsPageUpdateForumDescriptionMutation.graphql";

const Fragment = graphql`
  fragment TopicsPageFragment on Forum
  @argumentDefinitions(order: { type: "VotableOrder", defaultValue: HOT }) {
    id
    description
    slug
    owner {
      slug
      forumOwnerKind
    }
    viewerCanUpdate: viewerCan(action: UPDATE_FORUM)
    viewerCanCreateTopic: viewerCan(action: CREATE_TOPIC)
    ...ForumTopicsFragment @arguments(order: $order)
    ...SubjectSubscriptionButtonFragment
  }
`;

const UpdateForumDescriptionMutation = graphql`
  mutation TopicsPageUpdateForumDescriptionMutation(
    $forumId: ID!
    $description: String
  ) {
    updateForum(id: $forumId, input: { description: $description }) {
      node {
        id
        description
      }
    }
  }
`;

function DescriptionSection({
  forum: forumFragment,
}: {
  forum: TopicsPageFragment$key;
}) {
  const intl = useIntl();

  const forum = useFragment(Fragment, forumFragment);
  const [commitUpdateDescriptionMutation, isUpdateDescriptionMutationInFlight] =
    useMutation<TopicsPageUpdateForumDescriptionMutation>(
      UpdateForumDescriptionMutation,
    );

  if (!(forum.viewerCanUpdate || forum.description)) {
    return null;
  }

  return (
    <div className="mt-4!">
      <EditMarkdownSection
        id={forum.id}
        className="pb-0"
        canUseSuggestions={false}
        canEdit={forum.viewerCanUpdate}
        disabled={isUpdateDescriptionMutationInFlight}
        content={forum.description ?? ""}
        onSubmit={({ content }) =>
          new Promise<void>((resolve, reject) =>
            commitUpdateDescriptionMutation({
              variables: { forumId: forum.id, description: content || null },
              onCompleted: () => resolve(),
              onError: (error) => {
                logger.error(error);
                reject(
                  intl.formatMessage({
                    defaultMessage:
                      "An error occurred while saving the description. Please, try again later.",
                  }),
                );
              },
            }),
          )
        }
        canUploadFiles
      />
    </div>
  );
}

export interface TopicsPageProps {
  forum: TopicsPageFragment$key;
  initialOrder: VotableOrder;
  parent?: PathSegment;
}

export default function TopicsPage({
  forum: forumFragment,
  initialOrder,
  parent,
}: TopicsPageProps) {
  const { userId } = useAuth();
  const loginPath = useLoginRedirect();
  const forum = useFragment(Fragment, forumFragment);
  const [order, setOrder] = useState<VotableOrder>(initialOrder);
  const isDesktop = useMediaQuery(DESKTOP_MEDIA_QUERY);
  const [sticky, setSticky] = useState(false);
  const headerRef = useRef<HTMLDivElement | null>(null);

  const handleScroll = useCallback(() => {
    if (headerRef.current) {
      const headerTop = headerRef.current.getBoundingClientRect().top;
      setSticky(headerTop <= 0);
    }
  }, []);

  useEffect(() => {
    if (isDesktop) {
      setSticky(false);
      return;
    }

    window.addEventListener(
      "scroll",
      handleScroll,
      supportsPassiveEvents() ? { passive: true } : false,
    );

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [isDesktop, handleScroll]);

  return (
    <TopicVotableOrderContext.Provider value={order}>
      <div ref={headerRef} className="relative">
        <div
          className={cn(
            sticky && !isDesktop ? "sticky top-0 bg-white w-full" : undefined,
            sticky
              ? "flex flex-row justify-center items-center py-4 w-full"
              : "flex sm:flex-row flex-col items-center justify-between",
          )}
        >
          <div
            className={cn(
              "inline-flex justify-start items-center gap-1",
              !sticky ? "sm:pb-0 pb-4" : undefined,
            )}
          >
            <p className="text-grey-900 text-xl font-semibold leading-8 pb-0.5">
              <FormattedMessage defaultMessage="Topics" />
            </p>
            <VotableOrderSelect value={order} onChange={setOrder} />
          </div>
          <div className="flex flex-row items-center justify-between gap-3">
            <SubjectSubscriptionButton
              subject={forum}
              collapsed={sticky && !isDesktop}
            />
            {(!userId || forum.viewerCanCreateTopic) && (
              <Link
                size="md"
                to={
                  userId
                    ? getForumPath(
                        { ...forum, owner: { ...forum.owner, parent } },
                        "new",
                      )
                    : loginPath
                }
                rel={userId ? undefined : "nofollow"}
                state={{ order }}
                variant="secondary"
                className="text-sm"
              >
                <PlusIcon className="w-5 h-5 text-grey-700" />
                <span className={cn(sticky && !isDesktop ? "hidden" : "pl-2")}>
                  <FormattedMessage defaultMessage="New Topic" />
                </span>
              </Link>
            )}
          </div>
        </div>
        <DescriptionSection forum={forumFragment} />
        <div className="mt-4 pb-8">
          <ForumTopics forum={forum} order={order} parent={parent} />
        </div>
      </div>
    </TopicVotableOrderContext.Provider>
  );
}
